import { useEffect, useState } from 'react';
import { apiRequest } from '../utils/auth';
import './SubscriberSummary.css';

interface Breakdown { id: string; label: string; count: number }
interface Summary { total: number; channels: Breakdown[]; otherSources: Breakdown[] }
type SummaryState = { status: 'loading' } | { status: 'error' } | { status: 'ready'; summary: Summary };

export default function SubscriberSummary({ refreshVersion = 0 }: { refreshVersion?: number }) {
  const [state, setState] = useState<SummaryState>({ status: 'loading' });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    apiRequest<{ success: boolean; summary: Summary }>('/api/admin/users/subscriber-summary')
      .then(response => {
        if (!response.success || !response.summary) throw new Error();
        if (active) setState({ status: 'ready', summary: response.summary });
      }).catch(() => { if (active) setState({ status: 'error' }); });
    return () => { active = false; };
  }, [retry, refreshVersion]);
  const summary = state.status === 'ready' ? state.summary : null;

  return <section className="subscriber-summary" aria-label="Subscriber totals" aria-busy={state.status === 'loading'}>
    <div className="subscriber-summary-total">
      <span>Total subscribers</span>
      <strong>{summary ? summary.total.toLocaleString() : state.status === 'error' ? '—' : '…'}</strong>
      <small>Current paid-plan accounts · all users</small>
    </div>
    {state.status === 'error' ? <div role="alert" className="subscriber-summary-error">Unable to load subscriber totals.
      <button type="button" onClick={() => setRetry(value => value + 1)}>Retry</button>
    </div> : summary ? <div className="subscriber-summary-details">
      <dl aria-label="Subscription purchase channels">{summary.channels.map(channel => <div key={channel.id}>
        <dt>{channel.label}</dt><dd>{channel.count.toLocaleString()}</dd>
      </div>)}</dl>
      <div className="subscriber-summary-other-sources"><span>Other sources · included in total</span>
        <dl aria-label="Other subscriber sources">{summary.otherSources.map(source => <div key={source.id}>
          <dt>{source.label}</dt><dd>{source.count.toLocaleString()}</dd>
        </div>)}</dl>
      </div>
    </div> : <span role="status" className="subscriber-summary-loading">Loading subscriber totals…</span>}
  </section>;
}
